import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { gmsThemes } from './gms.theme';
import { ThemeNames } from './types';
import { GlobalStyles } from './GlobalStyles';
import stylesConfig from 'src/config';
import { useSelector } from 'react-redux';
import { AppState } from 'src/reducers';
import { adsparkThemes } from './adspark.theme';
import { telxiraThemes } from './telxira.theme';

// Theme registry with all available themes defined at app load
export const themeRegistry: Record<string, any> = {
  gms: gmsThemes, // Register the default theme
  // Add other themes here when defined
  adspark: adsparkThemes,
  telxira: telxiraThemes,
};

// Interface to include namespace selection
interface ThemeContextType {
  themeName: ThemeNames;
  namespace: string;
  setTheme: (name: ThemeNames) => void;
  setNamespace: (namespace: string) => void;
  availableNamespaces: string[];
}

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemeNames;
  initialNamespace?: string;
}

// Custom hook for theme classes
const useThemeClasses = (namespace: string, themeName: ThemeNames) => {
  useEffect(() => {
    const allThemeClasses = Object.keys(themeRegistry).flatMap((ns) => [`${ns}-theme-${themeName}`]);

    document.documentElement.classList.remove(...allThemeClasses);
    document.documentElement.classList.add(`${namespace}-theme-${themeName}`);
  }, [namespace, themeName]);
};

// Custom hook to initialize themes
const useInitializeTheme = (initialTheme?: ThemeNames, initialNamespace?: string) => {
  const [themeName, setThemeName] = useState<ThemeNames>(initialTheme || 'light');
  const [namespace, setNamespace] = useState<string>(initialNamespace || 'gms');

  useEffect(() => {
    // Initialize theme from localStorage if not provided
    if (!initialTheme) {
      const savedTheme = localStorage.getItem('theme') as ThemeNames;
      if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
        setThemeName(savedTheme);
      }
    }

    // Initialize namespace from localStorage if not provided
    if (!initialNamespace) {
      const savedNamespace = localStorage.getItem('theme-namespace');
      if (savedNamespace && themeRegistry[savedNamespace]) {
        setNamespace(savedNamespace);
      }
    }
  }, [initialTheme, initialNamespace]);

  return { themeName, setThemeName, namespace, setNamespace };
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({
  children,
  initialTheme = 'light',
  initialNamespace = stylesConfig.THEME!,
}: ThemeProviderProps) => {
  const customAppearance = useSelector((state: AppState) => state.appearance.custom);

  const hasAppearanceColors =
    customAppearance &&
    customAppearance.primaryMainColor &&
    customAppearance.secondaryMainColor;

  // Initialize theme state
  const { themeName, setThemeName, namespace, setNamespace } = useInitializeTheme(initialTheme, initialNamespace);

  // Manage theme classes
  useThemeClasses(namespace, themeName);

  // Get theme library
  const themeLibrary = themeRegistry[namespace] || themeRegistry.gms;
  const baseTheme = themeLibrary[themeName];

  const currentTheme = hasAppearanceColors
    ? {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: {
          normal: customAppearance.primaryLightColor ?? baseTheme.colors.primary.normal,
          medium: customAppearance.primaryMainColor ?? baseTheme.colors.primary.medium,
          dark: customAppearance.primaryDarkColor ?? baseTheme.colors.primary.dark,
        },
        secondary: {
          normal: customAppearance.secondaryLightColor ?? baseTheme.colors.secondary.normal,
          medium: customAppearance.secondaryMainColor ?? baseTheme.colors.secondary.medium,
          dark: customAppearance.secondaryDarkColor ?? baseTheme.colors.secondary.dark,
        },
        background: baseTheme.colors.background,
        technical: baseTheme.colors.technical,
        status: baseTheme.colors.status,
        decorative: baseTheme.colors.decorative,
      },
      typography: baseTheme.typography,
    }
    : baseTheme;

  // Theme setter
  const setTheme = (name: ThemeNames) => {
    setThemeName(name);
    localStorage.setItem('theme', name);

    // Update document theme classes
    document.documentElement.classList.remove(
      ...Object.keys(themeRegistry).flatMap((ns) => [`${ns}-theme-${themeName}`])
    );
    document.documentElement.classList.add(`${namespace}-theme-${name}`);
  };

  // Namespace setter
  const setNamespaceHandler = (ns: string) => {
    if (themeRegistry[ns]) {
      setNamespace(ns);
      localStorage.setItem('theme-namespace', ns);

      // Update document theme classes when namespace changes
      document.documentElement.classList.remove(
        ...Object.keys(themeRegistry).flatMap((ns) => [`${ns}-theme-${themeName}`])
      );
      document.documentElement.classList.add(`${ns}-theme-${themeName}`);
    } else {
      
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        themeName,
        namespace,
        setTheme,
        setNamespace: setNamespaceHandler,
        availableNamespaces: Object.keys(themeRegistry),
      }}
    >
      <EmotionThemeProvider theme={currentTheme}>
        <GlobalStyles />
        {children}
      </EmotionThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
